"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = Summary;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_native_1 = require("react-native");
const native_1 = require("@react-navigation/native");
const react_native_drawer_layout_1 = require("react-native-drawer-layout");
const react_markdown_1 = __importDefault(require("react-markdown"));
const lottie_react_native_1 = __importDefault(require("lottie-react-native"));
const store_1 = require("../../store");
const user_1 = require("../../store/selectors/user");
const document_1 = require("../../store/selectors/document");
const user_2 = require("../../slices/user");
const document_2 = require("../../slices/document");
const DeviceProvider_1 = require("../../providers/DeviceProvider");
const DocumentsProvider_1 = require("../../providers/DocumentsProvider");
const useDevice_1 = require("../../hooks/useDevice");
const useRequest_1 = __importDefault(require("../../hooks/useRequest"));
const useDocument_1 = __importDefault(require("../../hooks/useDocument"));
const useSummaryMarkdown_1 = __importDefault(require("../../hooks/useSummaryMarkdown"));
const ReadLoud_1 = __importDefault(require("../../component/ReadLoud"));
const ChatPanel_1 = __importDefault(require("../../component/ChatPanel"));
const NoteButton_1 = __importDefault(require("../../component/NoteButton"));
const SoundBiteButton_1 = __importDefault(require("../../component/SoundBiteButton"));
const ScrollCustomIndicator_1 = __importDefault(require("../../component/ScrollCustomIndicator"));
const Cards_1 = require("../../component/Cards");
const AppLoader_1 = __importDefault(require("../../component/AppLoader"));
const BigButton_1 = __importDefault(require("../../component/GradientButton/BigButton"));
const AppText_1 = require("../../component/AppText");
const Sources_1 = __importDefault(require("./Sources"));
const Notes_1 = __importDefault(require("./Notes"));
const document_3 = require("../../services/document");
const document_4 = require("../../helper/document");
const colors_1 = require("../../common/colors");
const spacing_1 = require("../../common/spacing");
const requestMock_1 = require("./test/requestMock");
require("./markdown.css");
const constants_1 = require("../../common/constants");
const ConfigureSoundBite_1 = require("../../component/ConfigureSoundBite");
const CONTENT_MAX_WIDTH = 668;
const CONTENT_PADDING = 32;
const MIN_WIDTH_FOR_SUMMARY = 302.5;
const IS_TEST = false;
function Summary({ route: { params }, navigation }) {
    const { isKeyboardOpened } = (0, react_1.useContext)(DeviceProvider_1.deviceContext);
    const isFocused = (0, native_1.useIsFocused)();
    const { isMobileScreen, mainWidth } = (0, useDevice_1.useDevice)();
    const dispatch = (0, store_1.useAppDispatch)();
    const { abortGenerationStreaming } = (0, DocumentsProvider_1.useDocumentsContext)();
    const { MarkdownQuotationFormatter, MarkdownListFormatter } = (0, useSummaryMarkdown_1.default)();
    const { request } = (0, useRequest_1.default)();
    const { startDocumentConversation } = (0, useDocument_1.default)();
    const documentJson = (0, store_1.useAppSelector)(document_1.selectDocumentJson);
    const document = (0, store_1.useAppSelector)((state) => (documentJson && (0, document_2.selectDocumentById)(state, documentJson?.character_id)));
    const selectedFileIds = (0, store_1.useAppSelector)(document_1.selectSelectedFileIds);
    const note = (0, store_1.useAppSelector)(document_1.selectNote);
    const streamingSummary = (0, store_1.useAppSelector)(document_1.selectDocumentStreaming);
    const selectedConversation = (0, store_1.useAppSelector)(user_1.selectConversation);
    const documentUploadComplete = (0, store_1.useAppSelector)(document_1.selectDocumentUploadComplete);
    const isConversationOpen = (0, store_1.useAppSelector)(document_1.selectIsDocumentConversationOpen);
    const isSourcesOpen = (0, store_1.useAppSelector)((state) => state.documents.isSourcesOpen);
    const isAudioConfigOpen = (0, store_1.useAppSelector)(document_1.selectIsAudioConfigOpen);
    const isPdfOpen = (0, store_1.useAppSelector)(document_1.selectIsPdfOpen);
    const isImageRegenerating = (0, store_1.useAppSelector)(document_1.selectIsImageLoading);
    const user = (0, store_1.useAppSelector)(user_1.selectUser);
    const [openChatTriggered, setOpenChatTriggered] = (0, react_1.useState)(false);
    const [isSpeechEnabled, setIsSpeechEnabled] = (0, react_1.useState)(false);
    const isDocumentMountedRef = (0, react_1.useRef)(false);
    const scrollRef = (0, react_1.useRef)(null);
    const wasActionParamTriggered = (0, react_1.useRef)(false);
    const [scrollViewWidth, setScrollViewWidth] = (0, react_1.useState)(0);
    const handleScrollViewLayout = (0, react_1.useCallback)((event) => {
        const { width } = event.nativeEvent.layout;
        setScrollViewWidth(width);
    }, []);
    //Notes State
    const [isNoteVisible, setIsNoteVisible] = (0, react_1.useState)(false);
    const handleNoteVisibilityChange = (0, react_1.useCallback)((isVisible) => {
        setIsNoteVisible(isVisible);
    }, []);
    const documentImage = (0, react_1.useMemo)(() => (documentJson && 'imageUrl' in documentJson
        ? documentJson.imageUrl
        : documentJson?.images[documentJson?.images.length - 1]), [documentJson]);
    const documentVideo = (0, react_1.useMemo)(() => (documentJson?.youtubeUrl || documentJson?.files?.[0]?.sourceType === 'youtube'
        && documentJson.files[0].sourceUrl), [documentJson]);
    const summaryMarkdown = (0, react_1.useMemo)(() => ((streamingSummary ?? documentJson) && ((0, jsx_runtime_1.jsx)(react_markdown_1.default, { className: 'summary-markdown', components: {
            h3({ children }) {
                return ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.titleContainer, children: (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles.contentTitle, children: children }) }));
            },
            p({ children }) {
                return ((0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: [styles.contentText, isMobileScreen && styles.mobileContentText], children: children }));
            },
            ul(props) {
                return (0, jsx_runtime_1.jsx)(MarkdownListFormatter, { ...props });
            },
            blockquote(props) {
                return (0, jsx_runtime_1.jsx)(MarkdownQuotationFormatter, { ...props });
            }
        }, children: (0, document_4.normalizeSummary)(streamingSummary ?? (documentJson && 'summaryResponse' in documentJson
            ? documentJson.summaryResponse
            : documentJson?.message) ?? '') }))), [streamingSummary, documentJson]);
    const scrollToSummaryTop = (0, react_1.useCallback)(() => {
        const scrollToSpeech = () => {
            if (scrollRef.current) {
                scrollRef.current.scrollTo({ y: 256, animated: true });
            }
        };
        if (isMobileScreen) {
            // Wait for the keyboard to appear before scrolling
            setTimeout(scrollToSpeech, 300);
        }
        else {
            scrollToSpeech();
        }
    }, [isMobileScreen]);
    const openAudio = (0, react_1.useCallback)(() => {
        if (documentJson) {
            if (documentJson. /* fiveMinWrapAudioUrl */podcastAudioUrl) {
                setIsSpeechEnabled(true);
                scrollToSummaryTop();
            }
            else {
                dispatch((0, document_2.toggleAudioConfigOpen)());
                // generateFiveMinutesAudio(documentJson, () => {})
            }
        }
        // scrollToSummaryTop();
    }, [scrollToSummaryTop, documentJson, dispatch]);
    (0, native_1.useFocusEffect)((0, react_1.useCallback)(() => {
        console.log('useFocusEffect');
        setOpenChatTriggered((prev) => { console.log(prev, 'openChatTriggered'); return false; });
        setIsSpeechEnabled((prev) => { console.log(prev, 'isSpeechEnabled'); return false; });
        setIsNoteVisible((prev) => { console.log(prev, 'isNoteVisible'); return false; });
        return () => {
            console.log('useFocusEffectUnmounted');
            dispatch((0, document_2.setIsSourcesOpen)(false));
            dispatch((0, document_2.setIsAudioConfigOpen)(false));
            dispatch((0, document_2.resetStreaming)());
            abortGenerationStreaming();
            wasActionParamTriggered.current = false;
            isDocumentMountedRef.current = false;
        };
    }, [abortGenerationStreaming, dispatch]));
    (0, react_1.useEffect)(() => {
        let wasUnmounted = false;
        const fetchSummary = async (conversationId) => {
            return request(IS_TEST ? requestMock_1.getMockSummary : document_3.getSummary, conversationId).then((document) => {
                if (!wasUnmounted && document) {
                    dispatch((0, document_2.setDocumentJson)(document));
                }
                ;
            });
        };
        if (user?.id && !streamingSummary && params?.conversationId && !documentJson) {
            fetchSummary(params.conversationId);
        }
        ;
        return () => { wasUnmounted = true; };
    }, [
        user,
        streamingSummary,
        documentJson,
        params,
        request,
    ]);
    (0, react_1.useEffect)(() => {
        let wasUnmounted = false;
        const fetchSharedSummary = (shareId, password) => {
            request(document_3.getSharedSummary, shareId, password).then(async (sharedDocument) => {
                if (!wasUnmounted && sharedDocument) {
                    dispatch((0, document_2.setDocumentJson)(sharedDocument));
                }
                ;
            });
        };
        if (user?.id
            && params?.shareId
            && !documentJson) {
            fetchSharedSummary(params.shareId, params.password);
        }
        ;
        return () => { wasUnmounted = true; };
    }, [
        user,
        documentJson,
        params,
        request,
    ]);
    (0, react_1.useEffect)(() => {
        if (params?.autoMessage) {
            dispatch((0, user_2.setAutoSentMessage)(params.autoMessage));
        }
    }, [params?.autoMessage, dispatch]);
    (0, react_1.useEffect)(() => {
        if (documentJson && streamingSummary && !params) {
            navigation.setParams({ conversationId: selectedConversation?.id });
            // openAudio();
        }
        ;
    }, [selectedConversation, documentJson, streamingSummary, /* openAudio */ , navigation, params]);
    (0, react_1.useEffect)(() => {
        if (!openChatTriggered
            && documentJson
            && (params?.conversationId || params?.openChat)
            && !isMobileScreen) {
            setOpenChatTriggered(true);
            startDocumentConversation({
                shareId: params?.shareId,
                ...(!('shareId' in documentJson) && { documentJson }),
            });
        }
        ;
    }, [documentJson, openChatTriggered, isMobileScreen, params, startDocumentConversation]);
    const handleNotePress = (0, react_1.useCallback)(async () => {
        if (isNoteVisible) {
            setIsNoteVisible(false);
        }
        else {
            setIsNoteVisible(true);
            scrollToSummaryTop();
        }
    }, [isNoteVisible, scrollToSummaryTop]);
    const handleAskAQuestion = (0, react_1.useCallback)(() => {
        setOpenChatTriggered(true);
        startDocumentConversation({
            shareId: params?.shareId,
            ...(documentJson && !('shareId' in documentJson) && { documentJson }),
        });
    }, [documentJson, startDocumentConversation, params]);
    const handleChatClose = (0, react_1.useCallback)(() => {
        dispatch((0, document_2.toggleDocumentConversationOpen)());
    }, [dispatch]);
    const handlePDFClose = (0, react_1.useCallback)(() => {
        dispatch((0, document_2.togglePdfOpen)());
    }, [dispatch]);
    const handleSpeechTriggerPress = (0, react_1.useCallback)(() => {
        if (isSpeechEnabled) {
            setIsSpeechEnabled(false);
        }
        else {
            openAudio();
        }
    }, [isSpeechEnabled, openAudio]);
    (0, react_1.useEffect)(() => {
        if (!wasActionParamTriggered.current && isFocused) {
            if (params?.notes) {
                wasActionParamTriggered.current = true;
            }
            ;
            if (params?.audio) {
                openAudio();
                wasActionParamTriggered.current = true;
            }
            ;
        }
    }, [isFocused, params, openAudio]);
    console.log(streamingSummary, documentJson, params, openChatTriggered, isDocumentMountedRef.current, isFocused, 'clearParams!!!');
    if (!streamingSummary
        && documentJson
        && documentJson.conversationId !== params?.conversationId
        && (!('shareId' in documentJson) || documentJson.shareId !== params?.shareId)
        && !isDocumentMountedRef.current
        && isFocused) {
        console.log('clearDocument', isDocumentMountedRef.current, openChatTriggered, isSpeechEnabled, isNoteVisible);
        dispatch((0, user_2.clearDocument)());
        dispatch((0, document_2.resetNote)());
        dispatch((0, document_2.closeDocumentPanels)());
    }
    ;
    if (isFocused) {
        isDocumentMountedRef.current = true;
    }
    const selectedFontId = (0, store_1.useAppSelector)((state) => state.fontSettings.selectedFontId);
    const selectedFont = constants_1.AVAILABLE_FONTS.find(font => font.id === selectedFontId);
    const fontFamily = selectedFont?.fontFamily || 'system font';
    return ((0, jsx_runtime_1.jsx)(react_native_drawer_layout_1.Drawer, { open: isSourcesOpen, drawerPosition: "right", layout: {
            width: mainWidth,
            height: '100%',
        }, onOpen: () => { }, onClose: () => { }, renderDrawerContent: () => (documentJson && ((0, jsx_runtime_1.jsx)(Sources_1.default, { document: documentJson, selectedFileIds: selectedFileIds }))), style: styles.drawerContainerStyle, drawerStyle: [
            styles.drawerStyle,
            isMobileScreen && { borderRadius: 0, width: '100%' }
        ], overlayStyle: [styles.overlayStyle, { backgroundColor: 'rgba(0, 0, 0, 0.5)' }], children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [styles.container, isMobileScreen ? { left: '50%' } : { left: '48%' }], children: [(0, jsx_runtime_1.jsx)(ScrollCustomIndicator_1.default, { ref: scrollRef, onLayout: handleScrollViewLayout, customIndicator: true, indicatorOverlay: true, style: [
                        styles.root,
                        isMobileScreen && styles.rootMobile,
                    ], contentContainerStyle: [
                        styles.content,
                        isMobileScreen && styles.contentMobile,
                        { width: '100%',
                            // (scrollViewWidth - 64) >= MIN_WIDTH_FOR_SUMMARY ? '100%' : '75%'
                        }
                    ], children: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: isMobileScreen ? styles.top : { marginTop: 32 }, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { children: [(0, jsx_runtime_1.jsx)(AppLoader_1.default, { isLoading: isImageRegenerating, showLoader: true }), (0, jsx_runtime_1.jsx)(Cards_1.ImageCard, { uri: documentVideo || documentImage || '', loading: !documentJson, alt: `Document Picture`, aspectRatio: 16 / 9, ...documentJson
                                                && !('shareId' in documentJson)
                                                && { document: documentJson }, media: documentVideo ? 'video' : 'image' }), isAudioConfigOpen
                                            && documentJson
                                            && ((0, jsx_runtime_1.jsx)(ConfigureSoundBite_1.ConfigureSoundBite, { document: documentJson }))] }) }), (!streamingSummary && !params) && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.processingContainer, children: [(0, jsx_runtime_1.jsx)(lottie_react_native_1.default, { source: require('../../assets/icons/lottie/Files.json'), autoPlay: true, loop: true, style: styles.lottieAnimation }), (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles.processingText, children: "Reading your document" })] })), documentJson && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.titleContainer, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles.contentTitle, children: "Summary" }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.documentButtons, children: [(0, jsx_runtime_1.jsx)(NoteButton_1.default, { onPress: handleNotePress, showText: (scrollViewWidth - 64) >= MIN_WIDTH_FOR_SUMMARY }), (0, jsx_runtime_1.jsx)(SoundBiteButton_1.default, { isLoading: document?.isPodcastGenerating === 'Y'
                                                    && !documentJson.podcastAudioUrl, onPress: handleSpeechTriggerPress, showText: (scrollViewWidth - 64) >= MIN_WIDTH_FOR_SUMMARY })] })] })), documentJson && ((0, jsx_runtime_1.jsx)(Notes_1.default, { isVisible: isNoteVisible, onClose: () => setIsNoteVisible(false), ...('shareId' in documentJson ? ({
                                    documentShareId: documentJson.shareId
                                }) : ({
                                    characterId: documentJson.character_id,
                                    conversationId: documentJson.conversationId
                                })) })), isSpeechEnabled && documentJson && ((0, jsx_runtime_1.jsx)(ReadLoud_1.default, { source: documentJson.podcastAudioUrl
                                    || documentJson.fiveMinWrapAudioUrl
                                    || documentJson })), summaryMarkdown] }) }), (!isConversationOpen || isMobileScreen)
                    && !isPdfOpen
                    // && isMounted
                    && documentJson
                    && (!streamingSummary || documentUploadComplete)
                    // && selectedConversation
                    && !isKeyboardOpened
                    && ((0, jsx_runtime_1.jsx)(BigButton_1.default, { onPress: handleAskAQuestion, containerStyle: styles.conversationButtonContainer, style: styles.conversationButton, children: "Ask a question" })), isConversationOpen
                    && selectedConversation
                    // && isMounted
                    && !isMobileScreen
                    && params
                    && ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.chatPanelContainer, children: (0, jsx_runtime_1.jsx)(ChatPanel_1.default, { conversation: selectedConversation }) }))] }) }));
}
;
const styles = react_native_1.StyleSheet.create({
    container: {
        maxWidth: 1440,
        transform: 'translateX(-50%)',
        gap: 16,
        flexDirection: 'row',
        justifyContent: 'center',
        flex: 1,
    },
    root: {
        width: '100%',
        maxWidth: CONTENT_MAX_WIDTH,
        alignSelf: 'center',
        paddingHorizontal: CONTENT_PADDING,
    },
    rootMobile: {
        paddingHorizontal: 20,
    },
    content: {
        gap: spacing_1.Spacing.SPACING_3XL,
        marginBottom: 108,
        minWidth: MIN_WIDTH_FOR_SUMMARY,
    },
    contentMobile: {
        marginTop: 24,
        marginBottom: 112,
    },
    top: {
        gap: 16
    },
    soundBiteButton: {
        padding: 12,
        backgroundColor: colors_1.Colors.DARK_700,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
    title: {
        color: 'white',
        fontSize: 40,
        fontWeight: '700',
        lineHeight: 48,
    },
    mobileTitle: {
        fontSize: 24,
        fontWeight: '700',
        lineHeight: 32,
    },
    contentSection: {
        gap: 32,
    },
    titleContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    contentTitle: {
        color: colors_1.Colors.white,
        fontSize: 20,
        fontWeight: '700',
        lineHeight: 28,
    },
    contentText: {
        color: colors_1.Colors.white,
        fontSize: 16,
        lineHeight: 30,
    },
    mobileContentText: {
        fontSize: 16,
        lineHeight: 24,
    },
    text: {
        color: colors_1.Colors.white,
        fontSize: 16,
        lineHeight: 30,
    },
    shimmmeringContainer: {
        gap: 40,
        marginTop: -16,
    },
    shimmeringTitlesSection: {
        gap: spacing_1.Spacing.SPACING_XL,
    },
    shimmeringContentSection: {
        gap: spacing_1.Spacing.SPACING_3XL,
    },
    skeleton: {
        borderRadius: 8,
    },
    skeletonTitle: {
        height: 32,
    },
    skeletonContent: {
        height: 200,
    },
    documentButtons: {
        flexDirection: 'row',
        gap: spacing_1.Spacing.SPACING_MD,
    },
    input: {
        width: '68.5%',
        position: 'absolute',
        bottom: 41,
        alignSelf: 'center',
    },
    inputMobile: {
        width: '100%',
        bottom: 0,
    },
    conversationButtonContainer: {
        width: '100%',
        maxWidth: 378,
        position: 'absolute',
        bottom: 24,
        paddingHorizontal: 20,
    },
    conversationButton: {
        paddingVertical: 10,
        paddingHorizontal: 24,
    },
    fakeInput: {
        position: 'absolute',
        width: '100%',
        height: '100%',
    },
    chatPanelContainer: {
        width: '100%',
        maxWidth: 494,
        marginVertical: 32,
        flex: 1,
        minWidth: MIN_WIDTH_FOR_SUMMARY,
    },
    iconContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#1CD4F120',
        borderRadius: 20,
        paddingVertical: 4,
        paddingHorizontal: 12,
    },
    processingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center',
        justifyContent: 'center',
        marginHorizontal: 16,
        borderRadius: 8,
        position: 'relative',
        backgroundColor: '#1CD4F120',
        paddingRight: 16,
        paddingLeft: 8,
        paddingVertical: 8,
    },
    lottieAnimation: {
        width: 36,
        height: 36,
        alignSelf: 'center',
    },
    processingText: {
        color: 'white',
        fontSize: 14,
        fontWeight: '500',
    },
    iconWrapper: {
        backgroundColor: '#1CD4F120',
        borderRadius: 12,
        width: 24,
        height: 24,
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconText: {
        color: 'white',
        fontSize: 14,
        fontWeight: '500',
        marginRight: 8,
    },
    drawerContainerStyle: {
        overflow: 'hidden',
    },
    drawerStyle: {
        backgroundColor: '#242427',
        width: 400,
        overflow: 'hidden',
        borderTopLeftRadius: 40,
        borderBottomLeftRadius: 40,
    },
    overlayStyle: {
        display: 'none',
    },
});
